import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Bell } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import api from '../../api/api';

const DashboardHeader = ({ user: propUser, stats: propStats, dashboardData, dataLoaded }) => {
  const { user: userContextData } = useUser();
  const navigate = useNavigate();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const notificationRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();
  
  // Check if current page is dashboard page
  const isDashboardPage = location.pathname === '/dashboard' || location.pathname === '/';
  
  // Priority: UserContext (always latest) > props (from parent) > dashboardData
  // UserContext is updated immediately when profile changes, so prioritize it
  const user = userContextData || propUser || dashboardData?.user || {};
  const stats = propStats || dashboardData?.stats || {};
  const userName = user?.name || 'Employer';
  
  // State for notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Fetch real notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get('/notifications');
        if (response.data.success) {
          const allNotifs = response.data.data;
          const recentNotifs = allNotifs.slice(0, 4);
          const unread = allNotifs.filter(n => n.unread).length;
          
          setNotifications(recentNotifs);
          setUnreadCount(unread);
        }
      } catch (err) {
        // Silently fail - notifications are optional
      }
    };
    
    fetchNotifications();
  }, []);
  
  // Update dropdown position when opened and on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right
        });
      }
    };

    if (notificationsOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [notificationsOpen]);

  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [notificationsOpen]);

  // Format timestamp to relative time
  const formatTime = (timestamp) => {
    const now = new Date();
    const notifDate = new Date(timestamp);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return notifDate.toLocaleDateString();
  };

  // If not on dashboard page, render a simplified header or nothing
  if (!isDashboardPage) {
    return (
      <header className="mb-8 animate-fade-in transition-all duration-300">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-black">{userName.split(' ')[0]}</h1>
          <div className="flex items-center gap-4">
            <button 
              ref={buttonRef}
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative p-2 text-gray-600 hover:text-black transition-colors"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
            
            {/* Notification Dropdown - Rendered via Portal */}
            {notificationsOpen && ReactDOM.createPortal(
              <div 
                ref={notificationRef}
                className="fixed w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-[9999] animate-fade-in"
                style={{ top: `${dropdownPosition.top}px`, right: `${dropdownPosition.right}px` }}
              >
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold text-black">Notifications</h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                            notif.unread ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {notif.unread && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-sm text-black">{notif.title}</p>
                              <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatTime(notif.timestamp)}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-8 text-center">
                        <p className="text-gray-500 text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>
                  <div className="p-3 text-center border-t border-gray-200">
                    <button 
                      onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/notifications');
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View All Notifications
                    </button>
                  </div>
                </div>,
              document.body
            )}
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-full overflow-hidden">
              <img 
                src={user?.avatar || 'https://i.pravatar.cc/150?img=68'} 
                alt={userName}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </header>
    );
  }
  
  // Full dashboard header for dashboard page
  return (
    <header className="mb-12 animate-fade-in transition-all duration-500 relative z-50">
      {/* Main Header Card with 3D floating effect */}
      <div className="bg-white rounded-xl p-6 shadow-[0_15px_30px_-10px_rgba(0,0,0,0.15),0_5px_15px_-5px_rgba(0,0,0,0.1)] mb-8 transform hover:translate-y-[-3px] transition-all duration-300 relative overflow-visible border border-gray-100">
        {/* Subtle 3D effect elements */}
        <div className="absolute inset-0 bg-white rounded-xl -z-10">
        </div>
        <div className="relative z-10">
          <div className="flex justify-between items-center">
            {/* Left side with greeting and character */}
            <div className="flex items-center gap-6">
              <div>
                <h1 className="text-3xl font-bold text-black mb-1">
                  Hello {userName.split(' ')[0]}!
                </h1>
                <p className="text-sm text-gray-600">It's good to see you again.</p>
              </div>
              
              {/* Character illustration */}
              <div className="w-32 h-32 ml-6 relative">
                <div className="absolute -inset-2 bg-white rounded-full border border-gray-100 shadow-sm"></div>
                <img 
                  src="/images/character-clipboard.png" 
                  alt="Character with clipboard" 
                  className="w-full h-full object-contain relative z-10"
                />
              </div>
            </div>
            
            {/* Right side with actions */}
            <div className="flex items-center gap-5">
              {/* Notifications Button */}
              <button 
                ref={buttonRef}
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 text-gray-600 hover:text-black transition-colors"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown - Rendered via Portal */}
              {notificationsOpen && ReactDOM.createPortal(
                <div 
                  ref={notificationRef}
                  className="fixed w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-[9999] animate-fade-in"
                  style={{ top: `${dropdownPosition.top}px`, right: `${dropdownPosition.right}px` }}
                >
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-black">Notifications</h3>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.map((notif) => (
                          <div 
                            key={notif.id} 
                            className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                              notif.unread ? 'bg-blue-50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {notif.unread && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-sm text-black">{notif.title}</p>
                                <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{formatTime(notif.timestamp)}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 text-center">
                          <p className="text-gray-500 text-sm">No notifications yet</p>
                        </div>
                      )}
                    </div>
                    <div className="p-3 text-center border-t border-gray-200">
                      <button 
                        onClick={() => {
                          setNotificationsOpen(false);
                          navigate('/notifications');
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        View All Notifications
                      </button>
                    </div>
                  </div>,
                document.body
              )}
              
              {/* User Avatar */}
              <div className="w-10 h-10 bg-white border border-gray-200 rounded-full overflow-hidden">
                <img 
                  src={user?.avatar || 'https://i.pravatar.cc/150?img=68'} 
                  alt={userName}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-6">
        {/* Posted Jobs */}
        <div className="bg-white p-6 rounded-xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.2),0_10px_20px_-5px_rgba(0,0,0,0.1)] transform hover:translate-y-[-5px] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] transition-all duration-300 border border-gray-50 relative overflow-hidden">
          {/* Enhanced 3D effect with real data indicator */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-gray-50 opacity-70">
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-gray-50 to-transparent opacity-40"></div>
          <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-black opacity-5 rounded-full blur-xl"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"></rect><line x1="16" x2="16" y1="2" y2="6"></line><line x1="8" x2="8" y1="2" y2="6"></line><line x1="3" x2="21" y1="10" y2="10"></line></svg>
                  <p className="text-sm font-medium text-black">Posted Jobs</p>
                </div>
                <p className="text-xs text-gray-500">Last 30 days</p>
              </div>
              <div className="text-4xl font-bold text-black">{stats.openPositions || 0}</div>
            </div>
          </div>
        </div>
        
        {/* AI Credits Left */}
        <div className="bg-white p-6 rounded-xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.2),0_10px_20px_-5px_rgba(0,0,0,0.1)] transform hover:translate-y-[-5px] hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] transition-all duration-300 border border-gray-50 relative overflow-hidden">
          {/* Enhanced 3D effect with real data indicator */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-gray-50 opacity-70">
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-gray-50 to-transparent opacity-40"></div>
          <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-black opacity-5 rounded-full blur-xl"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600"><path d="M12 2v4"></path><path d="M12 18v4"></path><path d="m4.93 4.93 2.83 2.83"></path><path d="m16.24 16.24 2.83 2.83"></path><path d="M2 12h4"></path><path d="M18 12h4"></path><path d="m4.93 19.07 2.83-2.83"></path><path d="m16.24 7.76 2.83-2.83"></path></svg>
                  <p className="text-sm font-medium text-black">AI Credits Left</p>
                </div>
                <p className="text-xs text-gray-500">Renews on {new Date(dashboardData?.aiCredits?.refreshDate || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
              </div>
              <div className="text-4xl font-bold text-black">{dashboardData?.aiCredits?.available || 0}</div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
